import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Loader2, Trash2, Mail, ChevronDown, Download, MailCheck, MailX } from "lucide-react";
import { http, formatError } from "@/lib/api";

const STATUSES = ["new", "contacted", "qualified", "won", "closed"];
const fmt = (d) => new Date(d).toLocaleString("en-GB", { dateStyle: "medium", timeStyle: "short" });

const toCsv = (rows) => {
  const cols = ["created_at", "status", "form_type", "name", "business_name", "email", "phone", "website", "services", "budget", "challenge", "additional", "source_page", "utm"];
  const esc = (v) => `"${String(Array.isArray(v) ? v.join("; ") : typeof v === "object" && v ? JSON.stringify(v) : v ?? "").replace(/"/g, '""')}"`;
  return [cols.join(","), ...rows.map((r) => cols.map((c) => esc(r[c])).join(","))].join("\n");
};

export const Enquiries = () => {
  const [rows, setRows] = useState(null);
  const [open, setOpen] = useState(null);

  const load = () => http.get("/admin/enquiries").then((r) => setRows(Array.isArray(r.data) ? r.data : [])).catch((e) => toast.error(formatError(e)));
  useEffect(() => { load(); }, []);

  const setStatus = async (id, status) => {
    try { await http.patch(`/admin/enquiries/${id}`, { status }); setRows((rs) => rs.map((r) => (r.id === id ? { ...r, status } : r))); }
    catch (e) { toast.error(formatError(e)); }
  };
  const remove = async (id) => {
    if (!window.confirm("Delete this enquiry permanently?")) return;
    try { await http.delete(`/admin/enquiries/${id}`); setRows((rs) => rs.filter((r) => r.id !== id)); toast.success("Deleted"); }
    catch (e) { toast.error(formatError(e)); }
  };
  const exportCsv = () => {
    const url = URL.createObjectURL(new Blob([toCsv(rows)], { type: "text/csv" }));
    const a = Object.assign(document.createElement("a"), { href: url, download: "enquiries.csv" });
    a.click();
    URL.revokeObjectURL(url);
  };

  if (!rows) return <Loader2 className="h-6 w-6 animate-spin text-[#7C3AED]" />;
  return (
    <div data-testid="admin-enquiries">
      <div className="flex items-center justify-between">
        <h2 className="text-2xl font-extrabold text-[#13052A]">Enquiries <span className="text-base font-semibold text-slate-500">({rows.length})</span></h2>
        {rows.length > 0 && <button onClick={exportCsv} className="btn-ghost !py-2.5" data-testid="admin-export-csv"><Download className="h-4 w-4" /> Export CSV</button>}
      </div>
      <ul className="mt-6 space-y-3">
        {rows.map((r) => (
          <li key={r.id} className="rounded-2xl border border-violet-200 bg-white" data-testid={`enquiry-row-${r.id}`}>
            <button onClick={() => setOpen(open === r.id ? null : r.id)} className="flex w-full items-center justify-between gap-4 p-5 text-left">
              <div className="min-w-0">
                <p className="truncate font-bold text-[#13052A]">{r.name} · {r.business_name}</p>
                <p className="mt-0.5 truncate text-xs text-slate-500">{fmt(r.created_at)} · {r.form_type} · {(r.services || []).join(", ")}</p>
              </div>
              <div className="flex shrink-0 items-center gap-2">
                {r.email_sent ? <MailCheck className="h-4 w-4 text-green-600" aria-label="Notification sent" /> : <MailX className="h-4 w-4 text-amber-600" aria-label="Notification not sent" />}
                <span className={`rounded-full px-2.5 py-1 text-xs font-bold ${r.status === "new" ? "bg-[#7C3AED] text-white" : "bg-violet-50 text-[#581C87]"}`}>{r.status}</span>
                <ChevronDown className={`h-4 w-4 transition-transform ${open === r.id ? "rotate-180" : ""}`} />
              </div>
            </button>
            {open === r.id && (
              <div className="grid gap-4 border-t border-violet-100 p-5 text-sm md:grid-cols-2">
                {[["Email", r.email], ["Phone", r.phone], ["Website", r.website], ["Budget", r.budget], ["Source page", r.source_page], ["Referrer", r.referrer],
                  ["UTM", Object.entries(r.utm || {}).map(([k, v]) => `${k}=${v}`).join(", ")]].map(([k, v]) => (
                  <p key={k}><span className="font-bold text-[#581C87]">{k}:</span> <span className="break-words text-slate-700">{v || "-"}</span></p>
                ))}
                <p className="md:col-span-2"><span className="font-bold text-[#581C87]">Biggest challenge:</span> <span className="whitespace-pre-line text-slate-700">{r.challenge}</span></p>
                {r.additional && <p className="md:col-span-2"><span className="font-bold text-[#581C87]">Additional:</span> <span className="whitespace-pre-line text-slate-700">{r.additional}</span></p>}
                <div className="flex flex-wrap items-center gap-2 md:col-span-2">
                  <select value={r.status} onChange={(e) => setStatus(r.id, e.target.value)} className="rounded-xl border border-violet-200 px-3 py-2 text-sm" data-testid={`enquiry-status-${r.id}`}>
                    {STATUSES.map((s) => <option key={s}>{s}</option>)}
                  </select>
                  <a href={`mailto:${r.email}`} className="btn-ghost !py-2" data-testid={`enquiry-reply-${r.id}`}><Mail className="h-4 w-4" /> Reply</a>
                  <button onClick={() => remove(r.id)} className="ml-auto rounded-full p-2 text-red-600 hover:bg-red-50" aria-label="Delete enquiry" data-testid={`enquiry-delete-${r.id}`}><Trash2 className="h-4 w-4" /></button>
                </div>
              </div>
            )}
          </li>
        ))}
        {!rows.length && <li className="rounded-2xl border border-dashed border-violet-300 p-10 text-center text-sm text-slate-500">No enquiries yet.</li>}
      </ul>
    </div>
  );
};
